import React from 'react';
import includes from 'lodash/includes';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { routerShape, withRouter } from 'react-router';
import { isValid, setMinutes, setHours, format } from 'date-fns';
import FocusLock from 'react-focus-lock';
import omit from 'lodash/omit';
import CollegeEventLocationContainer from '../CollegeEventLocation';
import Modal from '../../components/Modal';
import Ref from '../../components/Ref';
import Container from '../../components/Container';
import Grid from '../../components/Grid';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Dimmer from '../../components/Dimmer';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Form from '../../components/Form';
import Dropdown from '../../components/Dropdown';
import Message from '../../components/Message';
import Divider from '../../components/Divider';
import PageTitle from '../../components/PageTitle';
import Icon from '../../components/Icon';
import { amToUsTimezone, getUsTimezoneOptions, getTimeUTC, getTimeLocal } from './timezones';
import DayPickerInput from '../../components/DatePickerInput';
import {
  CAMINO_SUBSCRIPTION,
  REP_VISITS_SUBSCRIPTION,
  FIX_COLLEGE_EVENT_DATE_TIMEZONE,
} from '../Authorization/constants';
import { addHttpToUrl, isUrlValid } from '../../utils/url';
import EventAudience from './event-audience';
import { isCaminoEventsEnabled as hasCollegeEventsForCaminoFlag } from '../../utils/college-events';
import DisplayIn from './display-in';
import messages from './messages';

import './styles.css';

const hoursRegex = new RegExp('^([0-9]|0[0-9]|1[0-2]):[0-5][0-9][ap]$');
const timezoneOptions = getUsTimezoneOptions();

const stringHoursToAMPM = (timeStr) => {
  if (!timeStr) return 'AM';
  return includes(timeStr.toLowerCase(), 'p') ? 'PM' : 'AM';
};

const ampmToStringHours = (ampm) => (ampm === 'PM' ? 'p' : 'a');

const stringHoursToTime = (timeStr) => {
  if (!timeStr) return timeStr;
  let date = new Date();
  const timeParts = timeStr.split(/[:apAP]/);
  date = setHours(date, timeParts[0]);
  date = setMinutes(date, timeParts[1]);
  return format(date, 'hh:mm');
};

const timeToMillis = (time) => {
  const plus12 = time && time.includes('p');
  let hours = time ? parseInt(time.split(':')[0], 10) : 0;
  if (hours !== 12 && plus12) hours += 12;
  if (hours === 12 && !plus12) hours = 0;
  const minutes = time ? parseInt(time.split(/[:ap]/)[1], 10) : 0;
  return parseInt((hours * 3600000) + (minutes * 60000), 10);
};

const hasValidEndTime = (eventDateTime, eventEndDateTime, startTime, endTime) => {
  if (!endTime || !endTime.match(hoursRegex)) return false;
  const oneDay = 1000 * 60 * 60 * 24;
  const startDate = new Date(eventDateTime);
  const endDate = new Date(eventEndDateTime);
  const startDateUTC = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDateUTC = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const endsAnotherDay = endDateUTC / oneDay - startDateUTC / oneDay > 0;
  return endsAnotherDay || (timeToMillis(endTime) >= timeToMillis(startTime));
};

export class CollegeEventsEditEvent extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.object,
    viewer: PropTypes.object,
    node: PropTypes.object,
    createCollegeEvent: PropTypes.func.isRequired,
    updateCollegeEvent: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    enabledFeatures: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    heAccount: PropTypes.shape({
      activeSubscriptions: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    let action = props.router.location.pathname.split('/').slice(-2)[0];
    this.state = this.getStateFromProps(props);
    if (action === 'publish') {
      this.state.errors = this.validateEvent(this.getEventDiff());
      action = 'edit';
    }
    this.eventNameLabel = React.createRef();
    this.eventDateTimeLabel = React.createRef();
    this.eventRegistrationURL = React.createRef();
    this.eventDisplayIn = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.loaded === true) return;
    this.setState(this.getStateFromProps(nextProps));
  }

  getStateFromProps = (props) => {
    const event = props.node || { status: 'UNPUBLISHED' };
    const action = props.router.location.pathname.split('/').slice(-2)[0];
    const eventLocations = (props.viewer || {}).eventLocations || [];
    const eventFilters = (props.viewer || {}).eventFilters || [];
    const enableDisplayIn = hasCollegeEventsForCaminoFlag(props.enabledFeatures);
    const createDefaultDisplayIn = () => {
      const caminoEnabled = this.props.heAccount?.activeSubscriptions.includes(CAMINO_SUBSCRIPTION);
      const navianceEnabled = this.props.heAccount?.activeSubscriptions.includes(REP_VISITS_SUBSCRIPTION);
      return ({
        naviancestudent: navianceEnabled && !caminoEnabled,
        camino: caminoEnabled && !navianceEnabled,
      });
    };
    return {
      action,
      event,
      name: event.name,
      eventDescription: event.eventDescription,
      date: new Date(event.eventDateTime ? getTimeLocal(event.eventDateTime) : Date.now()),
      endDate: new Date(event.eventEndDateTime ? getTimeLocal(event.eventEndDateTime) : Date.now()),
      deadline: event.registrationDeadline ? new Date(getTimeLocal(event.registrationDeadline)) : null,
      startTime: stringHoursToTime(event.startTime),
      endTime: stringHoursToTime(event.endTime),
      registrationDeadlineTime: stringHoursToTime(event.registrationDeadlineTime),
      startTimeAMPM: stringHoursToAMPM(event.startTime),
      endTimeAMPM: stringHoursToAMPM(event.endTime),
      deadlineAMPM: stringHoursToAMPM(event.registrationDeadlineTime),
      timezone: amToUsTimezone(event.timezone),
      registrationUrl: event.registrationUrl,
      // ignores displayIn if feature is disabled
      ...(enableDisplayIn ? ({
        displayIn: omit(event.displayIn || createDefaultDisplayIn(), '__typename'),
      }) : {}),
      virtual: !!event.virtual,
      collegeLocation: !event.virtual && this.filterEventData(event.collegeEventLocation, eventLocations),
      collegeEventFilter: this.filterEventData(event.collegeEventFilter, eventFilters),
      saving: false,
      isCancelling: false,
      deadlineUpdated: false,
      deadlineTimeUpdated: false,
      isTimezoneFirstInteraction: true,
      errors: {},
    };
  };

  getEventDiff = () => {
    const newEvent = {};
    const useLocalDateForEvent = (this.props.enabledFeatures || []).includes(FIX_COLLEGE_EVENT_DATE_TIMEZONE);
    newEvent.status = this.state.event.status;
    newEvent.name = this.state.name;
    newEvent.eventDescription = this.state.eventDescription || '';
    newEvent.eventDateTime = getTimeUTC(new Date(this.state.date), useLocalDateForEvent);
    newEvent.eventEndDateTime = getTimeUTC(new Date(this.state.endDate), useLocalDateForEvent);
    newEvent.registrationDeadline = getTimeUTC(new Date(this.state.deadline), useLocalDateForEvent);
    newEvent.startTime = this.state.startTime + ampmToStringHours(this.state.startTimeAMPM);
    newEvent.endTime = this.state.endTime + ampmToStringHours(this.state.endTimeAMPM);
    newEvent.registrationDeadlineTime = this.state.registrationDeadlineTime + ampmToStringHours(this.state.deadlineAMPM);
    newEvent.timezone = this.state.timezone;
    newEvent.registrationUrl = addHttpToUrl(this.state.registrationUrl);
    if ('displayIn' in this.state) {
      newEvent.displayIn = this.state.displayIn;
    }
    newEvent.virtual = !!this.state.virtual;
    if (this.state.collegeLocation && !this.state.virtual) newEvent.collegeEventLocationId = this.state.collegeLocation.id;
    if (this.state.collegeEventFilter) newEvent.collegeEventFilterId = this.state.collegeEventFilter.id;
    if (this.state.virtual && this.props.node && !this.props.node.virtual) newEvent.collegeEventLocationId = null;
    if (this.state.action === 'edit') {
      newEvent.id = this.state.event.id;
      newEvent.version = this.state.event.version;
    }
    if (!this.state.deadline) {
      delete newEvent.registrationDeadline;
      delete newEvent.registrationDeadlineTime;
    }
    return newEvent;
  };

  // eslint-disable-next-line class-methods-use-this
  validateEvent(event) {
    const errors = {
      generalError: false,
      err: null,
    };
    errors.startTime = !event.startTime || !event.startTime.match(hoursRegex);
    errors.endTime = !hasValidEndTime(event.eventDateTime, event.eventEndDateTime, event.startTime, event.endTime);
    errors.registrationDeadlineTime = event.registrationDeadlineTime && !event.registrationDeadlineTime.match(hoursRegex);
    errors.collegeLocation = !event.virtual && !event.collegeEventLocationId;
    errors.registrationUrl = !isUrlValid(event.registrationUrl);
    errors.name = !event.name || event.name.length > 120;
    const plus12 = event.startTime && event.startTime.includes('p');
    let hours = event.startTime ? parseInt(event.startTime.split(':')[0], 10) : 0;
    if (hours !== 12 && plus12) hours += 12;
    if (hours === 12 && !plus12) hours = 0;
    const minutes = event.startTime ? parseInt(event.startTime.split(/[:ap]/)[1], 10) : 0;
    const plus12Reg = event.registrationDeadlineTime && event.registrationDeadlineTime.includes('p');
    let hoursReg = event.registrationDeadlineTime ? parseInt(event.registrationDeadlineTime.split(':')[0], 10) : 0;
    if (hoursReg !== 12 && plus12Reg) hoursReg += 12;
    if (hoursReg === 12 && !plus12Reg) hoursReg = 0;
    const minutesReg = event.registrationDeadlineTime ? parseInt(event.registrationDeadlineTime.split(/[:ap]/)[1], 10) : 0;
    const startDateTime = parseInt(event.eventDateTime + (hours * 3600000) + (minutes * 60000), 10);
    const registrationDateTime = parseInt(event.registrationDeadline + (hoursReg * 3600000) + (minutesReg * 60000), 10);
    errors.dates = (registrationDateTime || false) && startDateTime < registrationDateTime;
    errors.past = startDateTime < new Date().getTime();
    errors.registrationDeadlinePast = (registrationDateTime || false) && registrationDateTime < new Date().getTime();

    const enableDisplayIn = hasCollegeEventsForCaminoFlag(this.props.enabledFeatures);
    const hasCaminoSubscription = this.props.heAccount.activeSubscriptions.includes(CAMINO_SUBSCRIPTION);
    const hasNavianceSubscription = this.props.heAccount.activeSubscriptions.includes(REP_VISITS_SUBSCRIPTION);

    if (enableDisplayIn) {
      errors.displayIn =
        (!event.displayIn.naviancestudent || !hasNavianceSubscription) &&
        (!event.displayIn.camino || !hasCaminoSubscription);
    }
    return errors;
  }

  onChange = (e, { value, name } = {}) => {
    value = value || e.currentTarget.value;
    name = name || e.currentTarget.name;
    if (['startTime', 'endTime', 'registrationDeadlineTime'].includes(name)) {
      value = this.formatTimeInput(value, this.state[name] || '');
    }
    if (name === 'timezone') {
      this.setState({ isTimezoneFirstInteraction: false });
    }
    if (name === 'startTime' && !this.state.deadlineTimeUpdated) {
      this.setState({ startTime: value, registrationDeadlineTime: value, loaded: true });
    } else if (name === 'endTime') {
      this.setState({ endTime: value, loaded: true });
    } else if (name === 'registrationDeadlineTime') {
      this.setState({ registrationDeadlineTime: value, deadlineTimeUpdated: true, loaded: true });
    } else {
      this.setState({ [name]: value, loaded: true });
    }
  };

  formatTimeInput = (current, previous) => {
    if (previous.includes(':') && !current.includes(':')) return current;
    if (current.length === 2) return `${current}:`;
    if (current.length >= 3) return current.replace(/\D/g, '').match(/.{1,2}/g).join(':');
    return current;
  };

  setVirtual = (virtual) => {
    this.setState({ virtual });
  };

  onLocationChange = (location) => {
    this.setState({
      collegeLocation: location,
      loaded: true,
    });
  };

  onFilterChange = (filter) => {
    this.setState({ collegeEventFilter: filter, loaded: true });
  };

  onFailure = (err) => {
    this.setState({ errors: { generalError: true, err }, saving: false });
  };

  onSuccess = () => {
    this.setState({ saving: false });
    this.props.router.push('/college-events/view-events/unpublished');
  };

  onPublishSuccess = () => {
    this.setState({ saving: false });
    this.props.router.push('/college-events/view-events/published');
  };

  filterEventData = (input, values) => {
    if (!input || !Array.isArray(values)) return null;
    if (values.find((value) => value.id === input.id)) return input;
    return null;
  };

  saveEvent = async () => {
    const updatedEvent = this.getEventDiff();
    const shouldUpdateDraft = updatedEvent.status === 'UNPUBLISHED' && !!updatedEvent.id;
    const errors = this.validateEvent(updatedEvent);
    if (Object.values(errors).some((e) => e === true)) {
      this.setState({ errors });
    } else {
      this.setState({ saving: true });
      updatedEvent.status = 'UNPUBLISHED';
      const { createCollegeEvent, updateCollegeEvent } = this.props;
      const variables = {
        input: {
          collegeEvent: updatedEvent,
        },
      };
      if (shouldUpdateDraft) {
        await updateCollegeEvent({ variables }).then(this.onSuccess, this.onFailure);
      } else {
        delete variables.input.collegeEvent.id;
        delete variables.input.collegeEvent.version;
        await createCollegeEvent({ variables }).then(this.onSuccess, this.onFailure);
      }
    }
  };

  publishEvent = () => {
    const updatedEvent = this.getEventDiff();
    const errors = this.validateEvent(updatedEvent);
    if (Object.values(errors).some((e) => e === true)) {
      this.setState({ errors });
    } else {
      this.setState({ saving: true });
      updatedEvent.status = 'PUBLISHED';
      const { createCollegeEvent, updateCollegeEvent } = this.props;
      const variables = {
        input: {
          collegeEvent: updatedEvent,
        },
      };
      if (updatedEvent.id) {
        updateCollegeEvent({ variables }).then(this.onPublishSuccess, this.onFailure);
      } else {
        createCollegeEvent({ variables }).then(this.onPublishSuccess, this.onFailure);
      }
    }
  };

  handleDayClick = (day, { disabled }) => {
    if (day && day.setHours) day.setHours(0, 0, 0, 0);
    if (!disabled) {
      const state = { date: day };
      if (this.state.endDate < day) state.endDate = day;
      if (!this.state.deadlineUpdated) state.deadline = day;
      this.setState(state);
    }
  };

  handleEndDayClick = (day, { disabled }) => {
    if (day && day.setHours) day.setHours(0, 0, 0, 0);
    if (!disabled) this.setState({ endDate: day });
  };

  handleDeadlineClick = (day, { disabled }) => {
    if (day && day.setHours) day.setHours(0, 0, 0, 0);
    if (!disabled) {
      this.setState({ deadline: day, deadlineUpdated: true });
    }
  };

  cancelEditing = () => {
    this.props.router.goBack();
  };

  renderCancelMessage() {
    if (!this.state.isCancelling) return null;
    const title = this.props.intl.formatMessage(messages.cancelTitle);
    const subtitle = this.props.intl.formatMessage(messages.cancelSubtitle);
    return (
      <Modal style={{ marginTop: 0 }} data-cy="create-event-cancel-modal" open styleName="event-modal">
        <Modal.Content styleName="event-modal-content">
          <FocusLock autoFocus>
            <div>
              <div styleName="cancel-info" role="alert" aria-label={`${title}, ${subtitle}`}>
                <div styleName="cancel-title"><FormattedMessage {...messages.cancelTitle} /></div>
                <div styleName="cancel-subtitle"><FormattedMessage {...messages.cancelSubtitle} /></div>
              </div>
              <div styleName="row border" />
              <div styleName="cancel-buttons">
                <Button
                  data-cy="create-event-cancel-modal-reject-button"
                  type="button"
                  styleName="reject-cancel-button"
                  onClick={() => this.setState({ isCancelling: false })}
                >
                  <FormattedMessage {...messages.cancelRejectButton} />
                </Button>
                <Button
                  data-cy="create-event-cancel-modal-confirm-button"
                  type="button"
                  primary
                  onClick={this.cancelEditing}
                >
                  <FormattedMessage {...messages.cancelConfirmButton} />
                </Button>
              </div>
            </div>
          </FocusLock>
        </Modal.Content>
      </Modal>
    );
  }

  render() {
    const { intl, enabledFeatures, heAccount: { activeSubscriptions } } = this.props;
    const {
      action,
      name,
      eventDescription,
      date,
      endDate,
      deadline,
      startTime,
      endTime,
      registrationDeadlineTime,
      startTimeAMPM,
      endTimeAMPM,
      deadlineAMPM,
      timezone,
      registrationUrl,
      displayIn,
      virtual,
      collegeLocation,
      collegeEventFilter,
      errors,
      saving,
    } = this.state;

    const enableDisplayIn = hasCollegeEventsForCaminoFlag(enabledFeatures);
    const hasCaminoSubscription = activeSubscriptions.includes(CAMINO_SUBSCRIPTION);
    const hasNavianceSubscription = activeSubscriptions.includes(REP_VISITS_SUBSCRIPTION);

    const today = new Date();
    const eventDate = isValid(date) ? date : undefined;
    const eventEndDate = isValid(endDate) ? endDate : undefined;
    const deadlineDate = isValid(date) ? deadline : undefined;

    const createEventPageTitle = (<PageTitle subTitle="Create Event" title="Events" category="Intersect" />);
    const editEventPageTitle = (<PageTitle subTitle="Edit Event" title="Events" category="Intersect" />);
    const duplicateEventPageTitle = (<PageTitle subTitle="Duplicate Event" title="Events" category="Intersect" />);
    const detailsEventPageTitle = (<PageTitle subTitle="Details" title="Events" category="Intersect" />);

    const trigger = (
      <div
        aria-hidden="true"
      >{timezoneOptions.find((option) => option.value === timezone).text}
      </div>
    );

    return (
      <Dimmer.Dimmable>
        {action === 'create' && createEventPageTitle}
        {action === 'edit' && editEventPageTitle}
        {action === 'duplicate' && duplicateEventPageTitle}
        {action === 'details' && detailsEventPageTitle}
        <Dimmer
          role="progressbar"
          aria-busy={saving}
          inverted
          active={saving}
        >
          <Loader active={saving} />
        </Dimmer>
        {this.renderCancelMessage()}
        <Container styleName="container">
          <Header color="black" size="large" data-cy="college-events-edit-form-header">
            {action === 'details' && <FormattedMessage tagName="h1" {...messages.editEventViewDetails} />}
            {action === 'edit' && <FormattedMessage tagName="h1" {...messages.editEventEditEvent} />}
            {action === 'create' && <FormattedMessage tagName="h1" {...messages.editEventCreateEvent} />}
            {action === 'duplicate' && <FormattedMessage tagName="h1" {...messages.editEventDuplicateEvent} />}
          </Header>
          <Grid stackable width={16} verticalAlign="middle" styleName="content" id="content">
            <Grid.Row>
              <Header color="black" size="small" data-cy="college-events-edit-form-sub-header">
                <FormattedMessage tagName="h3" {...messages.editEventDetails} />
              </Header>
            </Grid.Row>
            <Grid.Row data-cy="college-events-edit-form-event-name">
              <Grid.Column width={2} styleName="event-details required">
                <span>
                  <FormattedMessage {...messages.editEventName} />
                </span>
              </Grid.Column>
              <Grid.Column width={9}>
                <Form.Input
                  type="text"
                  fluid
                  styleName="event-input-text"
                >
                  <input
                    ref={this.eventNameLabel}
                    placeholder="Enter a descriptive name for the event"
                    aria-label="Event Name"
                    styleName="column-width"
                    onChange={this.onChange}
                    id="name"
                    name="name"
                    value={name || ''}
                    maxLength="120"
                    disabled={action === 'details'}
                  />
                </Form.Input>
                {(() => {
                  if (errors.name) {
                    if (this.eventNameLabel && this.eventNameLabel.current != null) {
                      this.eventNameLabel.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                    }
                    return (
                      <Label role="tooltip" basic color="red" pointing>
                        <FormattedMessage {...messages.editEventEnterName} />
                      </Label>
                    );
                  } else {
                    return null;
                  }
                })()}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row data-cy="college-events-edit-form-event-datetime">
              <Grid.Column width={2} styleName="event-details required">
                <div id="Event-Date-Time"><FormattedMessage {...messages.editEventDateTime} /></div>
              </Grid.Column>
              <Grid.Column width={14}>
                <div styleName="row" ref={this.eventDateTimeLabel}>
                  <div styleName="row-item">
                    <div role="group" aria-labelledby="Event-Date-Time">
                      <DayPickerInput
                        aria-label="Event Start Date"
                        onDayChange={this.handleDayClick}
                        date={eventDate}
                        selectedDays={eventDate}
                        initialMonth={eventDate}
                        disabledDays={[{ before: today }]}
                        disabled={action !== 'details'}
                        styleName="column-width"
                        icon="calendar outline"
                      />
                    </div>
                    <div styleName="event-input-time-container" role="group" aria-labelledby="Event-Date-Time">
                      <Form.Input
                        styleName="event-input-time"
                        maxLength={5}
                        type="text"
                        value={startTime}
                        data-time={startTime}
                        onChange={this.onChange}
                        name="startTime"
                      >
                        <input
                          id="startTime"
                          placeholder="--:--"
                          aria-invalid={errors.startTime}
                          aria-required="true"
                          aria-label="Event Start Time"
                          styleName="time-width"
                          disabled={action === 'details'}
                        />
                      </Form.Input>
                      <button
                        type="button"
                        data-cy="college-events-edit-form-start-time-options"
                        unselectable="on"
                        tabIndex="0"
                        styleName="time-options"
                        aria-label={`Event Start Time - ${startTimeAMPM}`}
                        onClick={() => this.setState((previous) => {
                          const sameTime = previous.registrationDeadlineTime === previous.startTime;
                          const newStartTimeAMPM = previous.startTimeAMPM === 'AM' ? 'PM' : 'AM';
                          const newDeadlineAMPM = sameTime ? newStartTimeAMPM : previous.deadlineAMPM;
                          return {
                            startTimeAMPM: newStartTimeAMPM,
                            deadlineAMPM: newDeadlineAMPM,
                          };
                        })}
                      >
                        {startTimeAMPM}
                        <div styleName="time-arrows">
                          <Icon name="caret up" />
                          <Icon name="caret down" />
                        </div>
                      </button>
                    </div>
                  </div>
                  {(errors.eventDate || errors.past || errors.startTime) && (
                    <div styleName="row-error">
                      {errors.eventDate && (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventEnterEventDate} />
                        </Label>
                      )}
                      {(!errors.eventDate && errors.past) && (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventSelectFuture} />
                        </Label>
                      )}
                      {(() => {
                        if (!errors.eventDate && !errors.past && errors.startTime) {
                          if (this.eventDateTimeLabel && this.eventDateTimeLabel.current != null) {
                            this.eventDateTimeLabel.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                          }
                          return (
                            <Label role="tooltip" basic color="red" pointing="left">
                              <FormattedMessage {...messages.editEventStartTime} />
                            </Label>
                          );
                        } else {
                          return null;
                        }
                      })()}
                    </div>
                  )}
                  {(!errors.eventDate && !errors.past && !errors.startTime &&
                    <div>&nbsp; - &nbsp;</div>
                  )}
                  <div styleName="row-item">
                    <div role="group" aria-labelledby="Event-Date-Time">
                      <DayPickerInput
                        aria-label="Event End Date"
                        onDayChange={this.handleEndDayClick}
                        date={eventEndDate}
                        selectedDays={eventEndDate}
                        initialMonth={eventEndDate}
                        disabledDays={[{ before: eventDate }]}
                        disabled={action !== 'details'}
                        styleName="column-width"
                        icon="calendar outline"
                      />
                    </div>
                    <div styleName="event-input-time-container" role="group" aria-labelledby="Event-Date-Time">
                      <Form.Input
                        styleName="event-input-time"
                        maxLength={5}
                        type="text"
                        value={endTime}
                        data-time={endTime}
                        onChange={this.onChange}
                        name="endTime"
                      >
                        <input
                          id="endTime"
                          placeholder="--:--"
                          aria-invalid={errors.endTime}
                          aria-required="true"
                          aria-label="Event End Time"
                          styleName="time-width"
                          disabled={action === 'details'}
                        />
                      </Form.Input>
                      <button
                        type="button"
                        data-cy="college-events-edit-form-end-time-options"
                        unselectable="on"
                        tabIndex="0"
                        styleName="time-options"
                        aria-label={`Event End Time - ${endTimeAMPM}`}
                        onClick={() => this.setState((previous) => ({
                          endTimeAMPM: previous.endTimeAMPM === 'AM' ? 'PM' : 'AM',
                        }))}
                      >
                        {endTimeAMPM}
                        <div styleName="time-arrows">
                          <Icon name="caret up" />
                          <Icon name="caret down" />
                        </div>
                      </button>
                    </div>
                  </div>
                  {(errors.eventDate || errors.past || errors.endTime) && (
                    <div styleName="row-error">
                      {errors.eventDate && (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventEnterEventDate} />
                        </Label>
                      )}
                      {(() => {
                        if (!errors.eventDate && errors.past) {
                          if (this.eventDateTimeLabel && this.eventDateTimeLabel.current !== null) {
                            this.eventDateTimeLabel.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                          }
                          return (
                            <Label role="tooltip" basic color="red" pointing="left">
                              <FormattedMessage {...messages.editEventSelectFuture} />
                            </Label>
                          );
                        } else {
                          return null;
                        }
                      })()}
                      {(() => {
                        if (!errors.eventDate && !errors.past && errors.endTime) {
                          if (this.eventDateTimeLabel && this.eventDateTimeLabel.current !== null) {
                            this.eventDateTimeLabel.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                          }

                          if (errors.startTime) {
                            return (
                              <Label role="tooltip" basic color="red" pointing="left">
                                <FormattedMessage {...messages.editEventEndTime} />
                              </Label>
                            );
                          } else {
                            return (
                              <Label role="tooltip" basic color="red" pointing="above" style={{ marginLeft: '310px', marginTop: '10px' }}>
                                <FormattedMessage {...messages.editEventEndTime} />
                              </Label>
                            );
                          }
                        } else {
                          return null;
                        }
                      })()}
                    </div>
                  )}
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row data-cy="college-events-edit-form-event-timezone">
              <Grid.Column width={2} styleName="event-details required">
                <span>
                  <FormattedMessage {...messages.editEventTimezone} />
                </span>
              </Grid.Column>
              <Grid.Column width={7}>
                <Dropdown
                  data-cy="college-events-timezone-dropdown"
                  id="timezone"
                  name="timezone"
                  styleName="dropdown"
                  trigger={trigger}
                  selection
                  labeled
                  options={timezoneOptions}
                  value={timezone}
                  onChange={this.onChange}
                  disabled={action === 'details'}
                  onFocus={() => this.setState({ isTimezoneFirstInteraction: true })}
                  aria-label={!this.state.isTimezoneFirstInteraction ?
                    timezoneOptions.find((option) => option.value === timezone).id :
                    intl.formatMessage(({ ...messages.editEventTimezone }))}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row data-cy="college-events-edit-form-event-description">
              <Grid.Column width={2} styleName="event-details align-top">
                <FormattedMessage {...messages.editEventDescription} />
              </Grid.Column>
              <Grid.Column width={9}>
                <Ref innerRef={(ref) => { this.descriptionRef = ref; }}>
                  <Form.TextArea
                    onChange={this.onChange}
                    aria-label="Description"
                    id="eventDescription"
                    name="eventDescription"
                    value={eventDescription}
                    styleName="column-width"
                    rows="10"
                    placeholder="Enter the description of the event"
                    disabled={action === 'details'}
                  />
                </Ref>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row data-cy="college-events-edit-form-event-registrationurl">
              <Grid.Column width={2} styleName="event-details required">
                <span>
                  <FormattedMessage {...messages.editEventRegistrationURL} />
                </span>
              </Grid.Column>
              <Grid.Column width={9}>
                <Form.Input
                  fluid
                  aria-label="Registration URL"
                  styleName="column-width"
                >
                  <input
                    onChange={this.onChange}
                    value={registrationUrl}
                    id="registrationUrl"
                    name="registrationUrl"
                    aria-label="Registration URL"
                    placeholder="http://example.com/url"
                    disabled={action === 'details'}
                    ref={this.eventRegistrationURL}
                  />
                </Form.Input>
              </Grid.Column>
              <Grid.Column width={4}>
                {!!errors.registrationUrl && (
                  <Label role="tooltip" basic color="red" pointing="left">
                    {(() => {
                      if (this.eventRegistrationURL && this.eventRegistrationURL.current) {
                        this.eventRegistrationURL.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    })()}
                    <FormattedMessage {...messages.editEventRegistrationUrl} />
                  </Label>
                )}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row data-cy="college-events-edit-form-event-deadline">
              <Grid.Column width={2} styleName="event-details">
                <div id="RSVP-Deadline"><FormattedMessage {...messages.editEventDeadline} /></div>
              </Grid.Column>
              <Grid.Column width={7}>
                <div styleName="event-input-deadline-container" role="group" aria-labelledby="RSVP-Deadline">
                  <DayPickerInput
                    aria-label={intl.formatMessage({ ...messages.editEventDeadline })}
                    onDayChange={this.handleDeadlineClick}
                    date={deadlineDate}
                    selectedDays={deadlineDate}
                    initialMonth={deadlineDate}
                    disabledDays={[{ before: today }, { after: eventDate }]}
                    disabled={action !== 'details'}
                    styleName="column-width"
                    icon="calendar outline"
                  />
                  <div styleName="event-input-time-container" role="group" aria-labelledby="RSVP-Deadline">
                    <Form.Input
                      styleName="event-input-time"
                      maxLength={5}
                      type="time"
                      value={registrationDeadlineTime}
                      data-time={registrationDeadlineTime}
                      onChange={this.onChange}
                      name="registrationDeadlineTime"
                    >
                      <input
                        id="registrationDeadlineTime"
                        placeholder="--:--"
                        type="text"
                        aria-invalid={errors.registrationDeadlineTime}
                        aria-label="RSVP Deadline Time"
                        styleName="time-width"
                        disabled={action === 'details'}
                      />
                    </Form.Input>
                    <button
                      type="button"
                      data-cy="college-events-edit-form-deadline-time-options"
                      unselectable="on"
                      tabIndex="0"
                      styleName="time-options"
                      aria-label={`Event Registration Deadline - ${deadlineAMPM}`}
                      onClick={() => this.setState((previous) => ({
                        deadlineAMPM: previous.deadlineAMPM === 'AM' ? 'PM' : 'AM',
                      }))}
                    >
                      {deadlineAMPM}
                      <div styleName="time-arrows">
                        <Icon name="caret up" />
                        <Icon name="caret down" />
                      </div>
                    </button>
                  </div>
                  <div styleName="error-label-container">
                    <div styleName="error-label-content">
                      {errors.registrationDeadlineTime && (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventEnterDeadlineTime} />
                        </Label>
                      )}
                      {!errors.registrationDeadlineTime && errors.registrationDeadlinePast && (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventSelectFutureReg} />
                        </Label>
                      )}
                      {!errors.registrationDeadlineTime && !errors.registrationDeadlinePast && errors.dates && (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventSelectDates} />
                        </Label>
                      )}
                    </div>
                  </div>
                </div>
              </Grid.Column>
            </Grid.Row>
            {enableDisplayIn && (
              <Grid.Row data-cy="college-events-edit-form-event-displayin">
                <Grid.Column width={2} styleName="event-details required">
                  <span ref={this.eventDisplayIn}>
                    <FormattedMessage {...messages.editEventDisplayIn} />
                  </span>
                </Grid.Column>
                <Grid.Column width={13} styleName="event-displayin-controls">
                  <DisplayIn
                    value={displayIn}
                    onChange={this.onChange}
                    navianceEnabled={hasNavianceSubscription}
                    caminoEnabled={hasCaminoSubscription}
                  />
                  {(() => {
                    if (errors.displayIn) {
                      this.eventDisplayIn.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      return (
                        <Label role="tooltip" basic color="red" pointing="left">
                          <FormattedMessage {...messages.editEventEnterDisplayIn} />
                        </Label>
                      );
                    } else {
                      return null;
                    }
                  })()}
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Column width={16}>
              <Divider styleName="event-divider" />
            </Grid.Column>
            <Grid.Row data-cy="college-events-edit-form-event-location">
              <Grid.Column width={16} styleName="event-details required">
                <label htmlFor="collegeLocation"><FormattedMessage {...messages.editEventLocation} /></label>
              </Grid.Column>
              {action === 'details' ? (
                <div styleName="data-not-dropdown">
                  {collegeLocation ? collegeLocation.name : 'location name not found'}
                </div>
              ) :
                (
                  <Grid.Column width={7} styleName="dropsearch">
                    <CollegeEventLocationContainer
                      id="collegeLocation"
                      aria-labelledby={intl.formatMessage(messages.editEventLocation)}
                      name="collegeLocation"
                      onSelectionChange={this.onLocationChange}
                      setVirtual={this.setVirtual}
                      selectedLocation={collegeLocation}
                      virtual={virtual}
                    />
                    {errors.collegeLocation && (
                      <Label role="tooltip" basic color="red" pointing="left">
                        <FormattedMessage {...messages.editEventSelectLocation} />
                      </Label>
                    )}
                  </Grid.Column>
                )}
            </Grid.Row>
            <Grid.Column width={16}>
              <Divider styleName="event-divider" />
            </Grid.Column>
            <EventAudience
              intl={intl}
              action={action}
              onFilterChange={this.onFilterChange}
              collegeEventFilter={collegeEventFilter}
            />
            <Grid.Row data-cy="college-events-edit-form-event-actions">
              <Grid.Column width={16} textAlign="right">
                {action !== 'details' && (
                  <div styleName="buttons">
                    <Button
                      data-cy="create-event-form-cancel-button"
                      floated="left"
                      type="button"
                      loading={saving}
                      onClick={() => this.setState({ isCancelling: true })}
                    >
                      <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button
                      data-cy="create-event-form-draft-button"
                      styleName="save-button"
                      title="Save Draft"
                      loading={saving}
                      onClick={this.saveEvent}
                    >
                      <FormattedMessage {...messages.editEventSave} />
                    </Button>
                    <Button
                      data-cy="create-event-form-publish-button"
                      primary
                      styleName="publish-button"
                      title="Publish Now"
                      loading={saving}
                      onClick={this.publishEvent}
                    >
                      <FormattedMessage {...messages.editEventPublish} />
                    </Button>
                  </div>
                )}
              </Grid.Column>
            </Grid.Row>
            {errors.generalError && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message error>
                    <Message.Header>
                      {
                        (((errors.err || {}).graphQLErrors || [])[0] || {}).message ||
                        <FormattedMessage {...messages.editEventError} />
                      }
                    </Message.Header>
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Container>
      </Dimmer.Dimmable>
    );
  }
}

export default withRouter(injectIntl(CollegeEventsEditEvent));
